import { useState, useEffect, useCallback } from 'react';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  isNotificationSupported,
  getNotificationPermissionStatus 
} from '../firebase';

/**
 * Hook pour gérer les notifications push
 */
export function useNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [lastNotification, setLastNotification] = useState(null);

  // Check support on mount
  useEffect(() => {
    const supported = isNotificationSupported();
    setIsSupported(supported);
    
    if (supported) {
      setPermission(getNotificationPermissionStatus());
    }
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    if (!isSupported || permission !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      setLastNotification(payload);
      
      // Play notification sound
      playNotificationSound();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isSupported, permission]);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.log('Notifications not supported');
      return false;
    }

    const token = await requestNotificationPermission();
    
    if (token) {
      setFcmToken(token);
      setPermission('granted');
      return true;
    }
    
    setPermission(getNotificationPermissionStatus());
    return false;
  }, [isSupported]);

  return {
    permission,
    isSupported,
    fcmToken,
    lastNotification,
    requestPermission,
    clearLastNotification: () => setLastNotification(null)
  };
}

/**
 * Play notification sound
 */
function playNotificationSound() {
  try {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.log('Could not play notification sound:', error);
  }
}

/**
 * Component for notification permission prompt
 */
export function NotificationPrompt({ onGranted, onDismissed }) {
  const { permission, isSupported, requestPermission } = useNotifications();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show prompt if supported and not yet granted or dismissed
    const dismissedStorage = localStorage.getItem('notification_prompt_dismissed');
    if (isSupported && permission === 'default' && !dismissedStorage) {
      // Delay showing the prompt
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isSupported, permission]);

  const handleEnable = async () => {
    const success = await requestPermission();
    setShowPrompt(false);
    if (success && onGranted) onGranted();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('notification_prompt_dismissed', 'true');
    if (onDismissed) onDismissed();
  };

  if (!showPrompt || dismissed || permission !== 'default') return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-card border border-white/10 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <span className="text-xl">🔔</span>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-white text-sm">
              Activer les notifications
            </h4>
            <p className="text-xs text-white/60 mt-1">
              Soyez alerté des nouveaux matchs et messages instantanément !
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleEnable}
                className="px-4 py-1.5 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors"
              >
                Activer
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-1.5 bg-white/10 text-white/70 text-xs font-medium rounded-full hover:bg-white/20 transition-colors"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default useNotifications;
